import React from "react";

/** Privacy Policy — English (Last updated: April 17, 2026) */
const PrivacyEnglishBody: React.FC = () => (
  <>
    <p className="text-body leading-relaxed mb-8">
      MySipur (&quot;we&quot;, &quot;us&quot;, or &quot;our&quot;) respects your privacy and is committed to protecting your personal
      data. This Privacy Policy explains how we collect, use, store, and protect your information when you use our
      platform.
    </p>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">1. Information We Collect</h2>
      <p className="text-body font-semibold mb-2">a. Information You Provide</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-4">
        <li>Images you upload</li>
        <li>Text you submit (stories, descriptions)</li>
        <li>Email address (for account creation and communication)</li>
      </ul>
      <p className="text-body font-semibold mb-2">b. Automatically Collected Information</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>Basic technical data (IP address, browser type, device type)</li>
        <li>Usage data (interactions with the platform)</li>
      </ul>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">2. How We Use Your Information</h2>
      <p className="text-body leading-relaxed mb-3">We use your information solely to:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Provide and operate the platform</li>
        <li>Generate personalized digital books</li>
        <li>Process your content using AI tools</li>
        <li>Improve the functionality and performance of the service</li>
      </ul>
      <p className="text-body leading-relaxed">
        We do <strong>not</strong> use your content for advertising or marketing.
      </p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">3. AI Processing</h2>
      <p className="text-body leading-relaxed mb-3">By using the platform, you acknowledge that:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>Your uploaded content (including images and text) is processed by third-party AI providers, including OpenAI</li>
        <li>This processing is necessary to generate your requested content</li>
        <li>We do not use your data to train AI models</li>
      </ul>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">4. Data Storage</h2>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed">
        <li>User content is stored temporarily using Cloudflare R2 (object storage)</li>
        <li>Data is stored securely using industry-standard practices</li>
      </ul>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">5. Data Retention and Deletion</h2>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Uploaded images and content are deleted after the book creation process is completed</li>
        <li>We do not guarantee recovery after deletion</li>
      </ul>
      <p className="text-body leading-relaxed">You may request deletion of your data by contacting us.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">6. Data Sharing</h2>
      <p className="text-body leading-relaxed mb-3">We do not sell or share your personal data with third parties, except:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>AI service providers (for content generation)</li>
        <li>Service providers necessary to operate the platform</li>
      </ul>
      <p className="text-body leading-relaxed">All third-party access is limited to what is required to provide the service.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">7. Children&apos;s Data</h2>
      <p className="text-body leading-relaxed mb-3">The platform is intended for use by adults (18+).</p>
      <p className="text-body leading-relaxed mb-3">If you upload images of minors, you confirm that:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>You are the legal guardian, or</li>
        <li>You have obtained proper consent from the legal guardian</li>
      </ul>
      <p className="text-body leading-relaxed">We do not knowingly collect data directly from children.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">8. Security</h2>
      <p className="text-body leading-relaxed mb-3">We take reasonable measures to protect your data, including:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Secure data storage</li>
        <li>Encryption in transit (HTTPS)</li>
        <li>Access control</li>
      </ul>
      <p className="text-body leading-relaxed">However, no system is completely secure.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">9. Your Rights</h2>
      <p className="text-body leading-relaxed mb-3">Depending on your location, you may have rights to:</p>
      <ul className="list-disc ps-6 space-y-1 text-body leading-relaxed mb-3">
        <li>Access your data</li>
        <li>Request deletion of your data</li>
        <li>Request correction of inaccurate data</li>
      </ul>
      <p className="text-body leading-relaxed">To exercise these rights, contact us at the email below.</p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">10. International Users</h2>
      <p className="text-body leading-relaxed">
        If you access the platform from outside the United States, you do so at your own risk and are responsible for
        compliance with local laws.
      </p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">11. Changes to This Policy</h2>
      <p className="text-body leading-relaxed">
        We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated date.
      </p>
    </section>

    <section className="mb-8">
      <h2 className="text-xl font-bold text-heading mb-2">12. Contact</h2>
      <p className="text-body leading-relaxed">
        If you have any questions or requests regarding this Privacy Policy, contact us at:{" "}
        <a
          className="font-semibold text-brandNavy underline underline-offset-2 hover:text-brandOrange focus:outline-none focus:ring-2 focus:ring-brandOrange rounded"
          href="mailto:support@mysipur.app"
        >
          support@mysipur.app
        </a>
      </p>
    </section>
  </>
);

export default PrivacyEnglishBody;
